import { z } from 'zod';

const phrase = z.string().trim().min(1).max(60);

/** Most custom tones an account can have. */
export const MAX_CUSTOM_TONES = 20;
export const MAX_TONE_NAME_LENGTH = 40;
export const MAX_TONE_DESCRIPTION_LENGTH = 200;

/** A tone's own rules, applied to every version of a rewrite in that tone. */
export const StyleRulesSchema = z
  .object({
    terms: z
      .array(z.object({ use: phrase, insteadOf: z.array(phrase).min(1).max(6) }))
      .max(30)
      .default([])
      .describe('Preferred terms, each with the alternatives it replaces.'),
    avoid: z.array(phrase).max(30).default([]).describe('Words and phrases never to use.'),
    notes: z.string().trim().max(500).default('').describe('Any other rules, in plain words.'),
  })
  .meta({ id: 'StyleRules' });

export type StyleRules = z.infer<typeof StyleRulesSchema>;

export const EMPTY_STYLE_RULES: StyleRules = { terms: [], avoid: [], notes: '' };

export const isEmptyStyleRules = (rules: StyleRules): boolean =>
  rules.terms.length === 0 && rules.avoid.length === 0 && !rules.notes;

/** A tone as a writer defines it: how it sounds, and the rules it brings. */
export const CustomToneInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(MAX_TONE_NAME_LENGTH)
      .describe('What the tone is called, as tone menus list it.'),
    description: z
      .string()
      .trim()
      .max(MAX_TONE_DESCRIPTION_LENGTH)
      .default('')
      .describe(
        'How rewrites in this tone should sound, such as “warm and brief, like a helpful colleague”. When empty, the name says it.',
      ),
    rules: StyleRulesSchema.default(EMPTY_STYLE_RULES).describe(
      'Rules every version follows in this tone, the minor grammar fixes included.',
    ),
  })
  .meta({ id: 'CustomToneInput' });

export type CustomToneInput = z.infer<typeof CustomToneInputSchema>;

export const CustomToneSchema = CustomToneInputSchema.extend({ id: z.string() }).meta({
  id: 'CustomTone',
});

export type CustomTone = z.infer<typeof CustomToneSchema>;

export const CustomToneResponseSchema = z
  .object({ tone: CustomToneSchema })
  .meta({ id: 'CustomToneResponse' });

export const CustomTonesResponseSchema = z
  .object({ tones: z.array(CustomToneSchema).describe('Oldest first.') })
  .meta({ id: 'CustomTones' });
